import React from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import classnames from 'classnames';
import 'react-select/dist/react-select.css';

import ValidatableField from './ValidatableField';
import { scrollChildIntoView } from '../../utility/scrollIntoView';
import { removeDuplicates } from '../../utility/recentlyUsed';

import './DropdownField.css';

class DropdownFieldComponent extends ValidatableField {
  constructor(props) {
    super(props);
    this.state.defaultValue = props.value;
    this.state.inputText = '';
  }

  static get propTypes() {
    return {
      label: PropTypes.string.isRequired,
      value: PropTypes.any,
      mapFn: PropTypes.func,
      editing: PropTypes.bool,
      possibleValues: PropTypes.array,
      name: PropTypes.string.isRequired,
      filterField: PropTypes.bool,
      onChange: PropTypes.func,
      className: PropTypes.string,
      updateOnClick: PropTypes.bool.isRequired,
      renderer: PropTypes.func,
      recentlyUsed: PropTypes.array,
      frequent: PropTypes.array
    };
  }

  static get defaultProps() {
    return {
      mapFn: value => value,
      onChange: () => {},
      validationMessage: 'Invalid Dropdown Field',
      updateOnClick: true
    };
  }

  denoteUniqueValue(obj, denoteIdentifier) {
    const newObject = Object.assign({}, obj);
    if (denoteIdentifier) {
      newObject.uniqueValue = `${obj.value}-${denoteIdentifier}`;
      newObject.denoted = true;
    } else {
      newObject.uniqueValue = obj.value;
    }
    return newObject;
  }

  componentWillReceiveProps(props) {
    if (this.props.value !== props.value) {
      this.setState({ defaultValue: props.value }, () => this.validate());
    }
    this.setState({ inputText: '' });
  }

  onChange(selected) {
    const { mapFn, possibleValues, updateOnClick } = this.props;

    const newValue = selected ? possibleValues.find(v => mapFn(v).value === selected.value) : undefined;
    this.setState({ defaultValue: newValue }, () => {
      if (updateOnClick) {
        this.onUpdated();
      }
    });
  }

  validate() {
    const { required } = this.props;
    const { defaultValue } = this.state;

    if (required && !defaultValue) {
      this.setValid(false);
      return false;
    }

    this.setValid(true);
    return true;
  }

  showLabel() {
    const { editing, mapFn, filterField } = this.props;
    const { defaultValue } = this.state;
    const value = (mapFn && defaultValue) ? mapFn(defaultValue).value : undefined;
    return !filterField && (!editing || !!value);
  }

  onBlur() {
    const { updateOnClick } = this.props;

    if (!updateOnClick) {
      this.onUpdated();
    }
  }

  onUpdated() {
    const { onChange } = this.props;
    const { defaultValue } = this.state;
    if (this.validate()) {
      onChange(defaultValue);
    }
  }

  renderTopSeparator(option) {
    const { frequent, recentlyUsed } = this.props;
    return (
      <div className={frequent && !recentlyUsed ? 'DropdownField-frequent-topSeparator' : 'DropdownField-topSeparator'}>
        {option.label}
      </div>
    );
  }

  renderOption(option) {
    if (option.topSeparator) {
      return this.renderTopSeparator(option);
    }

    return (
      <div className={classnames('DropdownField-option', { denoted: option.denoted })}>
        {this.renderOptionItem(option)}
      </div>
    );
  }

  renderOptionItem(option) {
    const { renderer, optionRenderer } = this.props;

    if (optionRenderer) {
      return optionRenderer(this.reverseMap(option));
    }
    return renderer ? this.renderValue(option) : (option.longDisplay || option.label);
  }

  renderValue(option) {
    const { renderer } = this.props;

    return renderer(this.reverseMap(option));
  }

  reverseMap(option) {
    const { possibleValues, mapFn } = this.props;

    return option
      ? possibleValues.find(o => mapFn(o).value === option.value)
      : null;
  }

  onInputChange(inputText) {
    this.setState({ inputText });
  }

  getDropdownOptions() {
    const { mapFn, recentlyUsed } = this.props;
    const { inputText, defaultValue } = this.state;
    const currentValue = defaultValue ? mapFn(defaultValue) : undefined;
    let options;
    if (recentlyUsed && (!inputText || inputText.length === 0)) {
      // Need to ensure that at least have the current value in the options
      // or will not display
      options = currentValue ? [currentValue] : [];
      this.addRecentlyUsed(options);
      options = removeDuplicates(options);
    } else {
      options = this.addOptions();
    }

    return options;
  }

  addOptions() {
    const { possibleValues, mapFn, frequent } = this.props;
    let options = possibleValues && possibleValues.length > 0
      ? possibleValues.map(mapFn)
      : [];
    // TODO: Should also add the current value as an option just in case
    // cannot fetch static data for possibleValues

    if (frequent) {
      options = options.map(obj => this.denoteUniqueValue(obj));
    }

    this.prependFrequent(options);

    return options;
  }

  addRecentlyUsed(options) {
    const { recentlyUsed, mapFn } = this.props;
    if (recentlyUsed && recentlyUsed.length > 0 && options) {
      const recentlyUsedFormatted = recentlyUsed.map(mapFn);
      options.push(...recentlyUsedFormatted);
    }
    if (options.length > 0) {
      this.prependSeparator(options, 'Recently used');
    } else {
      this.prependSeparator(options, 'Type to search');
    }
  }

  prependFrequent(options) {
    const { frequent, mapFn } = this.props;
    if (frequent && options) {
      this.prependSeparator(options, 'All');
      const formattedFrequent = frequent.map(mapFn).map(obj => this.denoteUniqueValue(obj, 'frequent'));
      options.unshift(...formattedFrequent);
      this.prependSeparator(options, 'Frequent');
    }
  }

  prependSeparator(options, separatorMessage) {
    if (options) {
      options.unshift({ label: separatorMessage, disabled: true, topSeparator: true });
    }
  }

  filterOption(option, filterString) {
    if (!filterString) return true;

    const query = filterString.toLowerCase();
    const isDenotedOption = option.uniqueValue && option.uniqueValue !== option.value;

    if (query && (isDenotedOption || option.topSeparator)) {
      // If there's a search query, don't return duplicate options or separators
      // e.g. options from the 'frequent' list and the 'main' list
      return false;
    }
    if (option.label && option.label.toLowerCase().includes(query)) {
      return true;
    }
    return false;
  }

  renderControl() {
    const { mapFn, editing, name, className, label,
      filterField, required, placeholder, renderer,
      frequent, recentlyUsed } = this.props;
    const { defaultValue } = this.state;
    const placeholderText = placeholder || `${label} ${required ? '' : '(optional)'}`;
    const value = defaultValue ? mapFn(defaultValue).value : undefined;
    const options = this.getDropdownOptions();
    const useClass = classnames({ DropdownField: !filterField, 'is-disabled': !editing },
      `select-${name}`, className);

    if (!editing) {
      const displayOption = (value && options)
        ? options.find(o => o.value === value)
        : undefined;
      return (
        <span className={useClass}>
          { renderer
            ? renderer(defaultValue)
            : `${displayOption ? displayOption.label : ''}\u00a0`
          }
        </span>
      );
    }

    let element;

    return (
      <Select
        name={name}
        placeholder={editing ? placeholderText : ''}
        className={useClass}
        filterOption={this.filterOption}
        optionClassName={frequent && !recentlyUsed ? 'Dropdown-option-frequent' : 'Dropdown-option'}
        value={filterField ? undefined : value}
        valueKey={frequent ? 'uniqueValue' : 'value'}
        options={options.length > 0 ? options : undefined}
        clearable={!required}
        onChange={selected => this.onChange(selected)}
        onBlur={() => this.onBlur()}
        disabled={!editing}
        onInputChange={input => this.onInputChange(input)}
        optionRenderer={option => this.renderOption(option)}
        valueRenderer={renderer ? option => this.renderValue(option) : null}
        arrowRenderer={editing && !filterField ? undefined : () => {}}
        scrollMenuIntoView={false}
        ref={(el) => { element = el && el.wrapper; }}
        onOpen={() => scrollChildIntoView(element, '.Select-menu-outer')}
      />
    );
  }
}

export default DropdownFieldComponent;



// WEBPACK FOOTER //
// ./src/recordDetail/fields/DropdownField.js